/**
 * =============================================================================
 * SCRIPT-NAME:   AdsPerfDashboard.js
 * PROJEKT:       Freiseinapp - Admin-Zentrale
 * VERSION:       v2.1
 * DATUM:         2026-03-29 07:46
 *
 * CHANGELOG:
 *   v2.1 | 2026-03-29 08:04 — AZG-Block immer anzeigen (auch bei nur einer AZG)
 *   v2.0 | 2026-03-29 07:46
 *     - Keywords: AZG-Spalte ausgeblendet (_AZG intern für Gruppierung)
 *     - Schedule/Journey: nach Laden zugeklappt
 *     - Alle Bereiche: start geschlossen
 *     - Assets: AZG/Anzeigen-Blöcke auf-/zuklappbar
 *     - Sitelinks: Toggle eingebaut
 *     - AZG-Blöcke universell dynamisch (keine hardcodierten IDs)
 * DATUM:         2026-03-03
 *
 * AENDERUNGEN v1.3:
 * - Zweiter Zeitraum (Vergleich) im Filter ergaenzt
 * - Jeder Bereich hat Toggle: Einzeln / Vergleich
 * - Vergleich zeigt Periode1 | Periode2 | Differenz pro Spalte
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import "./AdsPerfDashboard.css";
import AdsDecisions from "./AdsDecisions";
import DecisionsSummary from "./DecisionsSummary";
import HaikuNotizEditor from "./HaikuNotizEditor";
import MiniJourney from "./MiniJourney";
import AuctionImport from "./AuctionImport";
import SearchTermsImport from "./SearchTermsImport";
import LocationsImport from "./LocationsImport";
import AudiencesImport from "./AudiencesImport";
import ChangesImport from "./ChangesImport";

// Reihenfolge laut berichtsstrategie.md (Plan-Strategie):
//   1. Kampagne-Performance · 2. Auktionsdaten · 3. Geräte · 4. Alter ·
//   5. Standorte · 6. Werbezeitplan · 7. Zielgruppen · 8. Suchbegriffe ·
//   9. Keywords · 10. Assets · 11. QF-Watch
// Synthese (Haiku-Wochen-Auswertung) bleibt ganz oben als zentrale KI-Karte.
const BEREICHE = [
    { key: 'haiku_reports', label: '🤖 Wochen-Auswertung' },
    { key: 'campaigns',     label: '📊 Kampagne' },
    { key: 'auktionen',     label: '⚔️ Auktionsdaten (Wer klaut Klicks?)' },
    { key: 'devices',       label: '📱 Geräte (Wo wird geklickt?)' },
    { key: 'age',           label: '🎂 Alter (Welches Alter ist aktivst?)' },
    { key: 'standorte',     label: '🗺️ Bundesländer' },
    { key: 'schedule',      label: '🕐 Werbezeitplan (Wann aktiv?)' },
    { key: 'zielgruppen',   label: '👥 Zielgruppen (Was interessiert?)' },
    { key: 'suchbegriffe',  label: '🔍 Suchbegriffe (Was suchen sie?)' },
    { key: 'keywords',      label: '🔑 Keywords (Welche zu teuer?)' },
    { key: 'assets',        label: '🖼️ Assets (Titel · Texte · Sitelinks)' },
    { key: 'qf_watch',      label: '🎯 QF-Watch (LP-Verbesserung)' },
];

const API = '/api/ads_perf_report.php';

// Zahlenspalten die verglichen werden sollen
const NUM_COLS = ['Impressionen','Klicks','Kosten','Conversions','CTR_pct','CPC','CPA',
                  'IS_pct','Rank_Lost_pct','Budget_Lost_pct','Impr','Impr_Vorwoche',
                  'Impr_Diff','Klicks_Diff','Kosten_Diff','QF'];

function getDefaultDates() {
    const today = new Date();
    const dow = today.getDay(); // 0=So, 1=Mo, ..., 5=Fr, 6=Sa

    // Letzten Donnerstag berechnen
    // Heute = Freitag (5): letzter Do = gestern (-1)
    // Heute = Samstag (6): letzter Do = vorgestern (-2)
    // Heute = Sonntag (0): letzter Do = -3
    // Heute = Montag (1): letzter Do = -4
    // etc.
    const daysToLastThursday = dow === 4 ? 7 : (dow + 3) % 7 || 7;
    const lastThursday = new Date(today);
    lastThursday.setDate(today.getDate() - daysToLastThursday);

    const lastFriday = new Date(lastThursday);
    lastFriday.setDate(lastThursday.getDate() - 6);

    const prevThursday = new Date(lastFriday);
    prevThursday.setDate(lastFriday.getDate() - 1);

    const prevFriday = new Date(prevThursday);
    prevFriday.setDate(prevThursday.getDate() - 6);

    const fmt = d => d.toISOString().split('T')[0];
    return {
        von1: fmt(lastFriday),
        bis1: fmt(lastThursday),
        von2: fmt(prevFriday),
        bis2: fmt(prevThursday),
    };
}

function isNumCol(col) {
    return NUM_COLS.includes(col);
}

function diff(a, b) {
    const n = parseFloat(a) - parseFloat(b);
    if (isNaN(n)) return '---';
    const sign = n > 0 ? '+' : '';
    return sign + (Number.isInteger(n) ? n : n.toFixed(2));
}

function diffClass(val) {
    const n = parseFloat(val);
    if (isNaN(n) || n === 0) return '';
    return n > 0 ? 'perf-pos' : 'perf-neg';
}

// CTR + CPC berechnen und Bewertung hinzufuegen
function enrichWithCTR(data) {
    if (!data || !data.length) return data;
    const cols = Object.keys(data[0]);
    const hasImpr  = cols.includes('Impressionen');
    const hasClick = cols.includes('Klicks');
    const hasKost  = cols.includes('Kosten');
    if (!hasImpr || !hasClick) return data;

    // Durchschnittliche CTR berechnen (nur Zeilen mit Impressionen > 0)
    const aktive = data.filter(r => parseFloat(r.Impressionen) > 0);
    const avgCTR = aktive.length
        ? aktive.reduce((s, r) => s + (parseFloat(r.Klicks) / parseFloat(r.Impressionen)), 0) / aktive.length
        : 0;

    return data.map(r => {
        const impr   = parseFloat(r.Impressionen) || 0;
        const clicks = parseFloat(r.Klicks) || 0;
        const kosten = parseFloat(r.Kosten) || 0;
        const ctr    = impr > 0 ? (clicks / impr * 100) : null;
        const cpc    = clicks > 0 ? (kosten / clicks) : null;

        let bewertung = '';
        if (impr >= 10 && clicks === 0)        bewertung = 'TOT';
        else if (impr > 0 && ctr < avgCTR * 0.5) bewertung = 'SCHWACH';
        else if (impr > 0 && ctr >= avgCTR * 1.5) bewertung = 'STARK';
        else if (impr > 0)                        bewertung = 'OK';

        return {
            ...r,
            CTR: ctr !== null ? ctr.toFixed(2) + '%' : '---',
            CPC: cpc !== null ? cpc.toFixed(2) + ' €' : '---',
            _bewertung: bewertung,
            _avgCTR: avgCTR,
        };
    });
}

function sortData(data, col, dir) {
    if (!col) return data;
    return [...data].sort((a, b) => {
        const an = parseFloat(a[col]), bn = parseFloat(b[col]);
        if (!isNaN(an) && !isNaN(bn)) return dir === 'asc' ? an - bn : bn - an;
        return dir === 'asc'
            ? String(a[col] ?? '').localeCompare(String(b[col] ?? ''))
            : String(b[col] ?? '').localeCompare(String(a[col] ?? ''));
    });
}

// Merge zwei Datensaetze: UNION (alle Zeilen aus P1 UND P2)
// Fehlende Seite = 0, damit Vergleich vollstaendig ist
function mergeResults(data1, data2, cols) {
    if (!data1.length && !data2.length) return [];

    // Schluessel-Spalten = alle nicht-numerischen Spalten
    const allCols = cols.length ? cols : Object.keys((data1[0] || data2[0] || {}));
    const keyCols = allCols.filter(c => !isNumCol(c));
    const numCols = allCols.filter(c => isNumCol(c));

    // Maps fuer schnellen Lookup
    const map1 = {}, map2 = {};
    data1.forEach(row => { map1[keyCols.map(c => row[c]).join('||')] = row; });
    data2.forEach(row => { map2[keyCols.map(c => row[c]).join('||')] = row; });

    // UNION aller Keys aus P1 + P2
    const allKeys = [...new Set([...Object.keys(map1), ...Object.keys(map2)])];

    return allKeys.map(k => {
        const row1 = map1[k] || {};
        const row2 = map2[k] || {};
        // Schluessel-Werte aus whichever has them
        const base = map1[k] || map2[k];
        const merged = {};
        keyCols.forEach(c => { merged[c] = base[c]; });
        numCols.forEach(c => {
            const v1 = row1[c] != null ? row1[c] : 0;
            const v2 = row2[c] != null ? row2[c] : 0;
            merged[c + '_P1']   = row1[c] != null ? row1[c] : 0;
            merged[c + '_P2']   = row2[c] != null ? row2[c] : 0;
            merged[c + '_DIFF'] = diff(v1, v2);
            // Status-Flag fuer spaetere Analyse-Ebenen
            if (map1[k] && !map2[k]) merged['_status'] = 'NEU';
            else if (!map1[k] && map2[k]) merged['_status'] = 'WEGGEFALLEN';
            else merged['_status'] = 'VORHANDEN';
        });
        return merged;
    });
}

export default function AdsPerfDashboard() {
    const defaults = getDefaultDates();
    const [von1, setVon1]           = useState(defaults.von1);
    const [bis1, setBis1]           = useState(defaults.bis1);
    const [von2, setVon2]           = useState(defaults.von2);
    const [bis2, setBis2]           = useState(defaults.bis2);
    const [campaigns, setCampaigns] = useState([]);
    const [selected, setSelected]   = useState([]);
    const [results, setResults]     = useState({});   // { key: { p1, p2 } }
    const [loading, setLoading]     = useState({});
    const [errors, setErrors]       = useState({});
    const [modus, setModus]         = useState({});   // { key: 'single'|'compare' }
    const [sortBy, setSortBy]       = useState({});
    const [collapsed, setCollapsed] = useState({aktionsplan:true, aenderungen:true, journey:true, campaigns:true, devices:true, schedule:true, age:true, keywords:true, suchbegriffe:true, assets:true, haiku_reports:true, auktionen:true, standorte:true, zielgruppen:true, qf_watch:true});
    const [collapsedData, setCollapsedData] = useState({});
    const [freshness, setFreshness] = useState({});
    const [kwActiveOnly, setKwActiveOnly] = useState(true);
    const [haikuDetails, setHaikuDetails] = useState({});  // { campaign_id: { loading, rows, error } }

    function toggleCollapse(key) {
        setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
    }

    // Hilfsformat für deutsches Datum dd.mm.yyyy
    const fmtDe = iso => {
        if (!iso || iso.length < 10) return '';
        const [y, m, d] = iso.split('-');
        return `${d}.${m}.${y}`;
    };
    const auswerteBereich = (von1 && bis1) ? `${fmtDe(von1)} – ${fmtDe(bis1)}` : '7 Tage';
    const haikuRangeBadge = () => (
        <span className="perf-fresh-badge perf-fresh-ok" title={`Auswertebereich: ${auswerteBereich}`}>
            🟢 {auswerteBereich}
        </span>
    );

    const dividerLabels = {
        haiku_reports: '🤖 Wochen-Auswertung',
        campaigns:    '📊 Kampagne',
        auktionen:    '⚔️ Auktionsdaten',
        devices:      '📱 Geräte',
        age:          '🎂 Alter',
        standorte:    '🗺️ Bundesländer',
        schedule:     '🕐 Werbezeitplan',
        zielgruppen:  '👥 Zielgruppen',
        suchbegriffe: '🔍 Suchbegriffe',
        keywords:     '🔑 Keywords',
        assets:       '🖼️ Assets',
        qf_watch:     '🎯 QF-Watch',
    };
    const [selectedRow, setSelectedRow] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('token') || '';
        const headers = { 'Authorization': 'Bearer ' + token };
        fetch('/api/ads_perf_campaigns_list.php', { headers })
            .then(r => r.json())
            .then(data => {
                setCampaigns(data);
                setSelected(data.map(c => String(c.campaign_id)));
            })
            .catch(() => setCampaigns([]));
        fetch('/api/ads_perf_freshness.php', { headers })
            .then(r => r.json())
            .then(d => { if (d && d.success) setFreshness(d.data || {}); })
            .catch(() => setFreshness({}));
    }, []);

    function renderFreshness(bereich) {
        const f = freshness[bereich];
        if (!f || !f.latest) return null;
        const age = f.age_days;
        let cls = 'perf-fresh-old';
        let icon = '🔴';
        if (age !== null && age <= 7)       { cls = 'perf-fresh-ok';   icon = '🟢'; }
        else if (age !== null && age <= 30) { cls = 'perf-fresh-warn'; icon = '🟡'; }
        return (
            <span className={'perf-fresh-badge ' + cls} title={'Letzter Datensatz: ' + f.latest + ' · ' + f.count + ' Zeilen gesamt'}>
                {icon} Stand {f.latest}
            </span>
        );
    }

    function toggleCampaign(id) {
        const sid = String(id);
        setSelected(prev =>
            prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]
        );
    }

    function toggleAll() {
        setSelected(selected.length === campaigns.length
            ? [] : campaigns.map(c => String(c.campaign_id)));
    }

    async function fetchBereich(von, bis, key) {
        const token = localStorage.getItem('token') || '';
        const apiBereich = key === 'haiku_reports' ? 'haiku' : key;
        const params = new URLSearchParams({
            von, bis,
            campaign_ids: selected.join(','),
            bereich: apiBereich
        });
        const res = await fetch(`${API}?${params}`, {
            headers: { 'Authorization': 'Bearer ' + token }
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || 'API Fehler');
        return data.data;
    }

    function toggleData(key) {
        setCollapsedData(prev => ({ ...prev, [key]: prev[key] !== false ? false : true }));
    }

    async function loadBereich(key) {
        if (!selected.length) return;
        setLoading(prev => ({ ...prev, [key]: true }));
        setErrors(prev => ({ ...prev, [key]: null }));
        try {
            let p1 = await fetchBereich(von1, bis1, key);
            let p2 = await fetchBereich(von2, bis2, key);
            // Keywords: AZG intern als _AZG für Gruppierung, nicht anzeigen
            if (key === 'keywords') {
                const hideAZG = r => { const {AZG, ...rest} = r; return {...rest, _AZG: AZG}; };
                if (p1) p1 = p1.map(hideAZG);
                if (p2) p2 = p2.map(hideAZG);
            }
            // Schedule: nach Laden zugeklappt
            if (key === 'schedule') {
                setCollapsedData(prev => ({ ...prev, schedule: true }));
            }
            setResults(prev => ({ ...prev, [key]: { p1, p2 } }));
        } catch (e) {
            setErrors(prev => ({ ...prev, [key]: e.message || 'Fehler beim Laden.' }));
        } finally {
            setLoading(prev => ({ ...prev, [key]: false }));
        }
    }

    function handleSort(bereichKey, col) {
        setSortBy(prev => {
            const cur = prev[bereichKey] || { col: null, dir: 'asc' };
            const dir = cur.col === col && cur.dir === 'asc' ? 'desc' : 'asc';
            return { ...prev, [bereichKey]: { col, dir } };
        });
    }

    function renderSingleTable(data, bereichKey) {
        if (!data || !data.length) return <p className="perf-empty">Keine Daten.</p>;
        const cols = Object.keys(data[0]);
        const sort = sortBy[bereichKey] || { col: null, dir: 'asc' };
        const sorted   = sortData(data, sort.col, sort.dir);
        const visCols  = cols.filter(c => !c.startsWith('_'));
        return (
            <div className="perf-table-wrap">
                <table className="perf-table">
                    <thead>
                        <tr>{visCols.map(c => (
                            <th key={c} className="perf-th-sortable" onClick={() => handleSort(bereichKey, c)}>
                                {c}{sort.col === c ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                            </th>
                        ))}</tr>
                    </thead>
                    <tbody>
                        {sorted.map((row, i) => {
                            const bew = row._bewertung || '';
                            // Retired-Detection: wenn Letztes-Datum > 3 Tage zurück → ausgegraut
                            let retiredCls = '';
                            if (row.Letztes) {
                                const ageDays = Math.floor((Date.now() - new Date(row.Letztes).getTime()) / 86400000);
                                if (ageDays > 3) retiredCls = 'row-retired';
                            }
                            const rowCls = bew === 'TOT'    ? 'row-tot'
                                         : bew === 'SCHWACH' ? 'row-schwach'
                                         : bew === 'STARK'   ? 'row-stark'
                                         : retiredCls;
                            return (
                                <tr key={i}
                                    className={[rowCls, selectedRow === `${bereichKey}-${i}` ? 'row-selected' : ''].filter(Boolean).join(' ')}
                                    onClick={() => setSelectedRow(selectedRow === `${bereichKey}-${i}` ? null : `${bereichKey}-${i}`)}>
                                    {visCols.map(c => <td key={c}>{row[c] ?? '---'}</td>)}

                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    function renderCompareTable(p1, p2, bereichKey) {
        if (!p1 || !p1.length) return <p className="perf-empty">Keine Daten fuer Zeitraum 1.</p>;
        const baseCols = Object.keys(p1[0]);
        const merged   = mergeResults(p1, p2, baseCols);
        if (!merged.length) return <p className="perf-empty">Keine Daten.</p>;

        const cols     = Object.keys(merged[0]);
        const sort     = sortBy[bereichKey] || { col: null, dir: 'asc' };
        const sorted   = sortData(merged, sort.col, sort.dir);

        // Spaltenheader schoener machen
        function colLabel(c) {
            if (c.endsWith('_P1'))   return c.replace('_P1','') + ' (Z1)';
            if (c.endsWith('_P2'))   return c.replace('_P2','') + ' (Z2)';
            if (c.endsWith('_DIFF')) return c.replace('_DIFF','') + ' +/-';
            return c;
        }

        // Analyse-Zusammenfassung berechnen (nur VORHANDEN fuer Gewinner/Verlierer)
        const imprDiffCol = cols.find(c => c.endsWith('_DIFF') && c.includes('Impr'));
        const vorhandene  = merged.filter(r => r._status === 'VORHANDEN');
        const summary = {
            neu:         merged.filter(r => r._status === 'NEU').length,
            weggefallen: merged.filter(r => r._status === 'WEGGEFALLEN').length,
            gewinner:    vorhandene.filter(r => imprDiffCol && parseFloat(r[imprDiffCol]) > 0).length,
            verlierer:   vorhandene.filter(r => imprDiffCol && parseFloat(r[imprDiffCol]) < 0).length,
        };

        const visibleCols = cols.filter(c => c !== '_status');

        return (
            <div>
                <div className="perf-analysis-bar">
                    {summary.neu > 0 && <span className="badge badge-new">🆕 {summary.neu} Neu</span>}
                    {summary.weggefallen > 0 && <span className="badge badge-gone">🚫 {summary.weggefallen} Weggefallen</span>}
                    {summary.gewinner > 0 && <span className="badge badge-win">📈 {summary.gewinner} Gewinner</span>}
                    {summary.verlierer > 0 && <span className="badge badge-lose">📉 {summary.verlierer} Verlierer</span>}
                </div>
                <div className="perf-table-wrap">
                    <table className="perf-table">
                        <thead>
                            <tr>
                                <th className="perf-th-sortable" onClick={() => handleSort(bereichKey, '_status')}>Status</th>
                                {visibleCols.map(c => (
                                    <th key={c} className={`perf-th-sortable${c.endsWith('_DIFF') ? ' perf-th-diff' : ''}`}
                                        onClick={() => handleSort(bereichKey, c)}>
                                        {colLabel(c)}{sort.col === c ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((row, i) => {
                                const status = row._status || '';
                                const rowCls = status === 'NEU' ? 'row-new'
                                             : status === 'WEGGEFALLEN' ? 'row-gone' : '';
                                return (
                                    <tr key={i}
                                        className={[rowCls, selectedRow === `cmp-${bereichKey}-${i}` ? 'row-selected' : ''].filter(Boolean).join(' ')}
                                        onClick={() => setSelectedRow(selectedRow === `cmp-${bereichKey}-${i}` ? null : `cmp-${bereichKey}-${i}`)}>
                                        <td>
                                            {status === 'NEU' && <span className="badge badge-new">Neu</span>}
                                            {status === 'WEGGEFALLEN' && <span className="badge badge-gone">Weg</span>}
                                            {status === 'VORHANDEN' && <span className="badge badge-ok">✓</span>}
                                        </td>
                                        {visibleCols.map(c => {
                                            const v = row[c] ?? '---';
                                            const cls = c.endsWith('_DIFF') ? diffClass(v) : '';
                                            return <td key={c} className={cls}>{v}</td>;
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Kollaps-State für AZG-Tabellen
    const [collapsedAZG, setCollapsedAZG] = React.useState({});
    function toggleAZG(key) {
        setCollapsedAZG(prev => ({ ...prev, [key]: prev[key] !== false ? false : true }));
    }

    // Gruppierung nach AZG — je eine eigene auf-/zuklappbare Tabelle
    function renderByAZGTable(rows, bereichKey) {
        if (!rows || rows.length === 0) return <p className="perf-empty">Keine Daten.</p>;
        // KW-Status-Filter: nur aktive zeigen wenn Toggle gesetzt
        var filtered = rows;
        if (bereichKey === 'keywords' && kwActiveOnly) {
            filtered = rows.filter(function(r) { return !r.Status || r.Status === 'ENABLED'; });
            if (filtered.length === 0) return <p className="perf-empty">Keine aktiven Keywords im Zeitraum.</p>;
        }
        var groups = {};
        filtered.forEach(function(r) {
            var azg = r._AZG || r.AZG || '—';
            if (!groups[azg]) groups[azg] = [];
            groups[azg].push(r);
        });
        var azgKeys = Object.keys(groups).sort();
        return (
            <div>
                {bereichKey === 'keywords' && (
                    <div className="perf-kw-toolbar">
                        <label className="perf-kw-toggle">
                            <input
                                type="checkbox"
                                checked={kwActiveOnly}
                                onChange={(e) => setKwActiveOnly(e.target.checked)}
                            />
                            <span>🟢 Nur aktive Keywords (ENABLED)</span>
                            <span className="perf-kw-toggle-hint">
                                ({rows.length - filtered.length} pausiert/entfernt ausgeblendet)
                            </span>
                        </label>
                    </div>
                )}
                {azgKeys.map(function(azg) {
                    var colKey = bereichKey + '__' + azg;
                    var isCollapsed = collapsedAZG[colKey] !== false;
                    return (
                        <div key={azg} className="perf-azg-group">
                            <div
                                className="perf-azg-label perf-azg-toggle"
                                onClick={() => toggleAZG(colKey)}
                                style={{cursor:'pointer'}}
                            >
                                <span>{isCollapsed ? '▶' : '▼'}</span>
                                {' '}📁 {azg} ({groups[azg].length})
                            </div>
                            {!isCollapsed && renderSingleTable(groups[azg], bereichKey)}
                        </div>
                    );
                })}
            </div>
        );
    }

    function renderAssetsTables(data) {
        if (!data || typeof data !== 'object') return <p className="perf-empty">Keine Daten.</p>;
        const { titles = [], texts = [], sitelinks = [] } = data;
        const enrichedTitles = enrichWithCTR(titles);
        const enrichedTexts  = enrichWithCTR(texts);

        // Bewertungs-Zusammenfassung
        function assetSummary(rows) {
            const tot     = rows.filter(r => r._bewertung === 'TOT').length;
            const schwach = rows.filter(r => r._bewertung === 'SCHWACH').length;
            const stark   = rows.filter(r => r._bewertung === 'STARK').length;
            return (
                <div className="perf-analysis-bar">
                    {stark  > 0 && <span className="badge badge-win">⭐ {stark} Stark</span>}
                    {schwach > 0 && <span className="badge badge-lose">⚠️ {schwach} Schwach</span>}
                    {tot    > 0 && <span className="badge badge-gone">💀 {tot} Tot (&gt;10 Impr, 0 Klicks)</span>}
                </div>
            );
        }

        // Gruppierung nach AZG → Anzeige
        function groupBy(rows, field) {
            const groups = {};
            rows.forEach(r => {
                const key = r[field] || '—';
                if (!groups[key]) groups[key] = [];
                groups[key].push(r);
            });
            return groups;
        }

        function renderByAZG(rows, bereich) {
            const azgGroups = groupBy(rows, 'AZG');
            const azgKeys = Object.keys(azgGroups);

            // Wenn nur eine AZG → Anzeigen-Ebene prüfen
            if (azgKeys.length <= 1) {
                const adGroups = groupBy(rows, 'Anzeige');
                const adKeys = Object.keys(adGroups);
                if (adKeys.length <= 1 || adKeys[0] === '—') {
                    const colKey = bereich + '__single';
                    const isCol  = collapsedAZG[colKey] !== false;
                    return (
                        <>
                            {assetSummary(rows)}
                            <div className="perf-azg-label perf-azg-toggle" onClick={() => toggleAZG(colKey)} style={{cursor:'pointer'}}>
                                <span>{isCol ? '▶' : '▼'}</span>{' '}📁 {azgKeys[0] || '—'} ({rows.length})
                            </div>
                            {!isCol && renderSingleTable(rows, bereich)}
                        </>
                    );
                }
                return (
                    <>
                        {assetSummary(rows)}
                        {adKeys.map(ad => {
                            const adColKey = bereich + '__single__' + ad;
                            const adIsCol  = collapsedAZG[adColKey] !== false;
                            return (
                                <div key={ad} className="perf-ad-group">
                                    <div className="perf-ad-label perf-azg-toggle" onClick={e => { e.stopPropagation(); toggleAZG(adColKey); }} style={{cursor:'pointer'}}>
                                        <span>{adIsCol ? '▶' : '▼'}</span>{' '}📄 {ad} ({adGroups[ad].length})
                                    </div>
                                    {!adIsCol && renderSingleTable(adGroups[ad], bereich)}
                                </div>
                            );
                        })}
                    </>
                );
            }

            // Mehrere AZGs → AZG → Anzeige
            return (
                <>
                    {assetSummary(rows)}
                    {azgKeys.map(azg => {
                        const adGroups = groupBy(azgGroups[azg], 'Anzeige');
                        const adKeys = Object.keys(adGroups);
                        const hasMultipleAds = adKeys.length > 1 && adKeys[0] !== '—';
                        const azgColKey = bereich + '__assets__' + azg;
                        const azgIsCollapsed = collapsedAZG[azgColKey] !== false;
                        return (
                            <div key={azg} className="perf-azg-group">
                                <div
                                    className="perf-azg-label perf-azg-toggle"
                                    onClick={() => toggleAZG(azgColKey)}
                                    style={{cursor:'pointer'}}
                                >
                                    <span>{azgIsCollapsed ? '▶' : '▼'}</span>
                                    {' '}📁 {azg} ({azgGroups[azg].length})
                                </div>
                                {!azgIsCollapsed && (hasMultipleAds ? (
                                    adKeys.map(ad => {
                                        const adColKey = bereich + '__ad__' + azg + '__' + ad;
                                        const adIsCollapsed = collapsedAZG[adColKey] !== false;
                                        return (
                                            <div key={ad} className="perf-ad-group">
                                                <div
                                                    className="perf-ad-label perf-azg-toggle"
                                                    onClick={e => { e.stopPropagation(); toggleAZG(adColKey); }}
                                                    style={{cursor:'pointer'}}
                                                >
                                                    <span>{adIsCollapsed ? '▶' : '▼'}</span>
                                                    {' '}📄 {ad} ({adGroups[ad].length})
                                                </div>
                                                {!adIsCollapsed && renderSingleTable(adGroups[ad], bereich)}
                                            </div>
                                        );
                                    })
                                ) : (
                                    renderSingleTable(azgGroups[azg], bereich)
                                ))}
                            </div>
                        );
                    })}
                </>
            );
        }

        return (
            <div>
                <h4 className="perf-sub-heading">Titel ({enrichedTitles.length})</h4>
                {renderByAZG(enrichedTitles, 'assets_titles')}
                <AdsDecisions bereich="assets_titles" selectedIds={selected} campaignList={campaigns} campaignId={selected.length === 1 ? selected[0] : null} />
                <h4 className="perf-sub-heading">Texte ({enrichedTexts.length})</h4>
                {renderByAZG(enrichedTexts, 'assets_texts')}
                <AdsDecisions bereich="assets_texts" selectedIds={selected} campaignList={campaigns} campaignId={selected.length === 1 ? selected[0] : null} />
                <div
                    className="perf-azg-label perf-azg-toggle"
                    onClick={() => toggleAZG('sitelinks')}
                    style={{cursor:'pointer', marginTop:'12px'}}
                >
                    <span>{collapsedAZG['sitelinks'] !== false ? '▶' : '▼'}</span>
                    {' '}🔗 Sitelinks ({sitelinks.length})
                </div>
                {collapsedAZG['sitelinks'] === false && renderSingleTable(sitelinks, 'assets_sitelinks')}
                {collapsedAZG['sitelinks'] === false && <AdsDecisions bereich="assets_sitelinks" selectedIds={selected} campaignList={campaigns} campaignId={selected.length === 1 ? selected[0] : null} />}
            </div>
        );
    }

    async function loadHaikuDetails(campaignId, von, bis) {
        setHaikuDetails(prev => ({ ...prev, [campaignId]: { loading: true } }));
        try {
            const token = localStorage.getItem('token') || '';
            const params = new URLSearchParams({
                von, bis,
                campaign_ids: String(campaignId),
                bereich: 'haiku_berichte',
            });
            const res = await fetch(`${API}?${params}`, { headers: { 'Authorization': 'Bearer ' + token } });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || 'API Fehler');
            setHaikuDetails(prev => ({ ...prev, [campaignId]: { loading: false, rows: data.data || [] } }));
        } catch (e) {
            setHaikuDetails(prev => ({ ...prev, [campaignId]: { loading: false, error: e.message } }));
        }
    }

    async function saveHaikuNotiz(berichtId, notiz, campaignId) {
        const token = localStorage.getItem('token') || '';
        try {
            const res = await fetch('/api/ads_haiku_notiz.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
                body: JSON.stringify({ id: berichtId, notiz }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || 'Fehler beim Speichern');
            // Optimistic update der Notiz im State
            setHaikuDetails(prev => {
                const cur = prev[campaignId];
                if (!cur?.rows) return prev;
                const rows = cur.rows.map(r => r.Id === berichtId ? { ...r, Notiz: notiz, _notizSaved: Date.now() } : r);
                return { ...prev, [campaignId]: { ...cur, rows } };
            });
        } catch (e) {
            alert('Notiz konnte nicht gespeichert werden: ' + e.message);
        }
    }

    function renderHaikuReports(rows) {
        if (!Array.isArray(rows) || !rows.length) {
            return <p className="perf-empty">Keine Haiku-Reports im gewählten Zeitraum. Generierung läuft Freitag 05:20 (Cron Step 13).</p>;
        }
        const statusColor = s => {
            if (s === 'OK')   return { bg: '#e6f4ea', border: '#34a853', label: '✅ OK' };
            if (s === 'WARN') return { bg: '#fef7e0', border: '#fbbc04', label: '⚠️ WARN' };
            if (s === 'ALERT')return { bg: '#fce8e6', border: '#ea4335', label: '🚨 ALERT' };
            return { bg: '#f1f3f4', border: '#9aa0a6', label: s || '—' };
        };
        const fmtNum = (v, suffix = '') => {
            if (v === null || v === undefined || v === '') return '—';
            const n = parseFloat(v);
            if (isNaN(n)) return String(v);
            return (Number.isInteger(n) ? n : n.toFixed(2)) + suffix;
        };
        const parseJson = v => {
            if (!v) return null;
            if (typeof v === 'object') return v;
            try { return JSON.parse(v); } catch { return null; }
        };
        const efMatchBadge = score => {
            const n = parseInt(score, 10);
            if (isNaN(n)) return null;
            const bg = n >= 70 ? '#34a853' : n >= 40 ? '#fbbc04' : '#ea4335';
            return <span style={{ background: bg, color: '#fff', padding: '2px 10px', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 600 }}>eF-Match {n}/100</span>;
        };
        // Reihenfolge laut berichtsstrategie.md
        const berichtLabels = {
            kampagne_azg:   '📊 Kampagne',
            auktionen:      '⚔️ Auktionsdaten (Wer klaut Klicks?)',
            geraete:        '📱 Geräte (Wo wird geklickt?)',
            alter:          '🎂 Alter (Welches Alter ist aktivst?)',
            standorte:      '🗺️ Bundesländer',
            werbezeitplan:  '🕐 Werbezeitplan (Wann aktiv?)',
            zielgruppen:    '👥 Zielgruppen (Was interessiert?)',
            suchbegriffe:   '🔍 Suchbegriffe (Was suchen sie?)',
            keywords:       '🔑 Keywords (Welche QF/Kosten?)',
            assets:         '🖼️ Assets (Titel/Texte/Sitelinks)',
            qf_watch:       '🎯 QF-Watch (LP-Verbesserung über QF)',
            aenderungen:    '📝 Änderungen (Kontext)',
        };
        return (
            <div className="perf-haiku-reports">
                {rows.map((r, i) => {
                    const sc = statusColor(r.Status);
                    return (
                        <div key={i} style={{
                            border: '1px solid ' + sc.border,
                            borderLeft: '6px solid ' + sc.border,
                            background: sc.bg,
                            borderRadius: '6px',
                            padding: '14px 18px',
                            marginBottom: '16px',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                                <h4 style={{ margin: 0, fontSize: '1.05rem' }}>{r.Kampagne}</h4>
                                <span style={{ fontSize: '0.85rem', color: '#5f6368' }}>
                                    {sc.label} · Run {r.Run}
                                </span>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px', fontSize: '0.9rem' }}>
                                <div>
                                    <strong>30 Tage:</strong>{' '}
                                    {fmtNum(r['Kosten 30T'], ' €')} · {fmtNum(r['Conv 30T'])} Conv ·
                                    CTR {fmtNum(r['CTR 30T %'], ' %')} · IS {fmtNum(r['IS 30T %'], ' %')} ·
                                    Lost-Rank {fmtNum(r['IS-Verlust Rang 30T %'], ' %')}
                                </div>
                                <div>
                                    <strong>7 Tage:</strong>{' '}
                                    {fmtNum(r['Kosten 7T'], ' €')} · {fmtNum(r['Conv 7T'])} Conv ·
                                    CTR {fmtNum(r['CTR 7T %'], ' %')} · IS {fmtNum(r['IS 7T %'], ' %')}
                                </div>
                            </div>

                            <div style={{ fontSize: '0.85rem', color: '#3c4043', marginBottom: '10px' }}>
                                <strong>Strategie:</strong> {r.Strategie || '—'} ·
                                <strong> Budget:</strong> {fmtNum(r.Budget, ' €')} ·
                                <strong> KWs:</strong> {fmtNum(r['KWs aktiv'])} aktiv / {fmtNum(r['KWs pausiert'])} pausiert ·
                                <strong> Top-Zeitslot:</strong> {r['Stärkster Zeitslot'] || '—'} ·
                                <strong> Top-Alter:</strong> {r['Stärkste Altersgruppe'] || '—'}
                            </div>

                            <div style={{ padding: '10px', background: '#fff', borderRadius: '4px', marginBottom: '8px' }}>
                                <div style={{ fontSize: '0.8rem', color: '#5f6368', marginBottom: '4px' }}>🤖 KI-Analyse</div>
                                <div style={{ fontSize: '0.92rem', whiteSpace: 'pre-wrap' }}>{r.Analyse || '—'}</div>
                            </div>

                            <div style={{ padding: '10px', background: '#fef7e0', borderRadius: '4px', marginBottom: '8px', border: '1px solid #fbbc04' }}>
                                <div style={{ fontSize: '0.8rem', color: '#5f6368', marginBottom: '4px' }}>💡 Empfehlung Gebote / Budget</div>
                                <div style={{ fontSize: '0.92rem', whiteSpace: 'pre-wrap', fontWeight: 500 }}>{r.Empfehlung || '—'}</div>
                            </div>

                            <div style={{ padding: '8px 10px', borderRadius: '4px', fontSize: '0.85rem', fontStyle: 'italic', color: '#5f6368' }}>
                                <strong>7-Tage Sanity-Check:</strong> {r['Sanity-Check'] || '—'}
                            </div>

                            {(r['Conv KW'] || r['Conv SB'] || r['Problematische SB'] || r['Schwache Assets']) && (
                                <details style={{ marginTop: '8px', fontSize: '0.83rem', color: '#5f6368' }}>
                                    <summary style={{ cursor: 'pointer' }}>Details (Conv-KW · Problematische SB · Schwache Assets)</summary>
                                    <div style={{ paddingTop: '8px' }}>
                                        {r['Conv KW']           && <div><strong>Conv KW:</strong> {r['Conv KW']}</div>}
                                        {r['Conv SB']           && <div><strong>Conv SB:</strong> {r['Conv SB']}</div>}
                                        {r['Problematische SB'] && <div><strong>Problematische SB:</strong> {r['Problematische SB']}</div>}
                                        {r['Schwache Assets']   && <div><strong>Schwache Assets:</strong> {r['Schwache Assets']}</div>}
                                    </div>
                                </details>
                            )}

                            {/* v3.0 — 11 Detail-Berichte + User-Profil + eF-Match */}
                            {(() => {
                                const det = haikuDetails[r['campaign_id'] || r.Kampagne];
                                const key = r['campaign_id'] || r.Kampagne;
                                return (
                                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #d0d7de' }}>
                                        {!det && (
                                            <button
                                                className="perf-btn-small"
                                                onClick={async () => {
                                                    // Wir brauchen campaign_id — aus rows ist nur Name + KPIs verfügbar.
                                                    // Lookup in campaigns-Liste
                                                    const camp = campaigns.find(c => c.campaign_name === r.Kampagne);
                                                    if (!camp) {
                                                        setHaikuDetails(prev => ({ ...prev, [key]: { error: 'Kampagne nicht gefunden in Liste' } }));
                                                        return;
                                                    }
                                                    loadHaikuDetails(camp.campaign_id, von1, bis1);
                                                }}
                                            >📊 11 Detail-Berichte + User-Profil laden</button>
                                        )}
                                        {det?.loading && <span style={{ marginLeft: '10px', fontSize: '0.85rem', color: '#5f6368' }}>Lädt …</span>}
                                        {det?.error && <span style={{ color: '#ea4335', fontSize: '0.85rem' }}>Fehler: {det.error}</span>}
                                        {det?.rows && (() => {
                                            const synth = det.rows.find(x => x.BerichtTyp === 'synthese');
                                            const berichte = det.rows.filter(x => x.BerichtTyp !== 'synthese');
                                            const up = parseJson(synth?.UserProfil);
                                            return (
                                                <>
                                                    {synth && (
                                                        <div style={{ background: '#e8f0fe', padding: '12px 14px', borderRadius: '6px', marginBottom: '10px' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                                <strong style={{ fontSize: '0.95rem' }}>👤 User-Profil + eF-Abgleich</strong>
                                                                {efMatchBadge(synth.eFMatchScore)}
                                                            </div>
                                                            {up && typeof up === 'object' && (
                                                                <div style={{ fontSize: '0.85rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '4px 12px', marginBottom: '8px' }}>
                                                                    {Object.entries(up).map(([k, v]) => (
                                                                        <div key={k}><strong>{k}:</strong> {typeof v === 'string' ? v : JSON.stringify(v)}</div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {/* Synthese-Analyse (CPA + Intent + eF zusammen) */}
                                                            {synth.Analyse && (
                                                                <div style={{ fontSize: '0.85rem', background: '#fff', padding: '8px 10px', borderRadius: '4px', marginBottom: '8px', whiteSpace: 'pre-wrap' }}>
                                                                    {synth.Analyse}
                                                                </div>
                                                            )}
                                                            {synth.Top3 && (
                                                                <div style={{ marginTop: '8px', background: '#fff8e1', padding: '8px 10px', borderRadius: '4px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                                                                    <strong>🎯 Top-3 Empfehlungen (Ziel: CPA &lt; 5 €):</strong>{'\n'}{synth.Top3}
                                                                </div>
                                                            )}
                                                            {/* Synthese-Notiz als eigene Editor-Box */}
                                                            <div style={{ marginTop: '10px' }}>
                                                                <HaikuNotizEditor
                                                                    berichtId={synth.Id}
                                                                    initialNotiz={synth.Notiz || ''}
                                                                    onSave={(notiz) => saveHaikuNotiz(synth.Id, notiz, r['campaign_id'] || r.Kampagne)}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div style={{ fontSize: '0.85rem', color: '#5f6368', marginBottom: '6px' }}>11 Detail-Berichte (Reihenfolge laut Strategie):</div>
                                                    {berichte.map((b, j) => {
                                                        const bsc = statusColor(b.Status);
                                                        const gebot = parseJson(b.Gebotsempfehlung);
                                                        const campKey = r['campaign_id'] || r.Kampagne;
                                                        return (
                                                            <details key={j} style={{ marginBottom: '6px', background: '#fff', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                                                                <summary style={{ cursor: 'pointer', padding: '8px 12px', fontSize: '0.88rem' }}>
                                                                    {berichtLabels[b.BerichtTyp] || b.BerichtTyp}
                                                                    <span style={{ marginLeft: '10px', fontSize: '0.78rem', color: bsc.border }}>{bsc.label}</span>
                                                                    {b.Effizienz && <span style={{ marginLeft: '6px', fontSize: '0.78rem', color: '#5f6368' }}>· Effizienz: {b.Effizienz}</span>}
                                                                    {b.Notiz && <span style={{ marginLeft: '6px', fontSize: '0.78rem', color: '#1a73e8' }}>· 📝 Notiz</span>}
                                                                </summary>
                                                                <div style={{ padding: '10px 12px', fontSize: '0.85rem', borderTop: '1px solid #f0f0f0' }}>
                                                                    {b.Analyse && <div style={{ marginBottom: '8px' }}>{b.Analyse}</div>}
                                                                    {gebot && typeof gebot === 'object' && Object.keys(gebot).length > 0 && (
                                                                        <div style={{ background: '#fef7e0', padding: '8px', borderRadius: '4px', marginBottom: '6px' }}>
                                                                            <strong>Gebotsempfehlung:</strong>
                                                                            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                                                                {Object.entries(gebot).map(([k, v]) => <li key={k}><strong>{k}:</strong> {String(v)}</li>)}
                                                                            </ul>
                                                                        </div>
                                                                    )}
                                                                    <HaikuNotizEditor
                                                                        berichtId={b.Id}
                                                                        initialNotiz={b.Notiz || ''}
                                                                        onSave={(notiz) => saveHaikuNotiz(b.Id, notiz, campKey)}
                                                                    />
                                                                </div>
                                                            </details>
                                                        );
                                                    })}
                                                </>
                                            );
                                        })()}
                                    </div>
                                );
                            })()}
                        </div>
                    );
                })}
            </div>
        );
    }

    function renderAssetsCompare(p1, p2) {
        if (!p1 || typeof p1 !== 'object') return <p className="perf-empty">Keine Daten.</p>;
        const sections = [
            { label: 'Titel',    d1: p1.titles    || [], d2: p2?.titles    || [], key: 'cmp_titles' },
            { label: 'Texte',    d1: p1.texts     || [], d2: p2?.texts     || [], key: 'cmp_texts' },
            { label: 'Sitelinks',d1: p1.sitelinks || [], d2: p2?.sitelinks || [], key: 'cmp_sitelinks' },
        ];
        return (
            <div>
                {sections.map(({ label, d1, d2, key }) => (
                    <div key={key}>
                        <h4 className="perf-sub-heading">{label} ({d1.length})</h4>
                        {renderCompareTable(d1, d2, key)}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="perf-page">
            <div className="perf-box">

                <header className="perf-header">
                    <h2>Ads Performance Dashboard</h2>
                </header>

                <section className="perf-filter">
                    {/* Zeitraum 1 */}
                    <div className="perf-filter-row">
                        <span className="perf-period-label">Zeitraum 1</span>
                        <label>Von</label>
                        <input type="date" value={von1} onChange={e => setVon1(e.target.value)} />
                        <label>Bis</label>
                        <input type="date" value={bis1} onChange={e => setBis1(e.target.value)} />
                    </div>
                    {/* Zeitraum 2 */}
                    <div className="perf-filter-row">
                        <span className="perf-period-label">Zeitraum 2</span>
                        <label>Von</label>
                        <input type="date" value={von2} onChange={e => setVon2(e.target.value)} />
                        <label>Bis</label>
                        <input type="date" value={bis2} onChange={e => setBis2(e.target.value)} />
                    </div>

                    {/* Kampagnen */}
                    <div className="perf-campaigns">
                        <div className="perf-campaigns-header">
                            <span className="perf-filter-label">Kampagnen</span>
                            <button className="perf-btn-small" onClick={toggleAll}>
                                {selected.length === campaigns.length ? 'Alle ab' : 'Alle an'}
                            </button>
                        </div>
                        <div className="perf-campaign-list">
                            {campaigns.map(c => (
                                <label key={c.campaign_id} className="perf-campaign-item">
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(String(c.campaign_id))}
                                        onChange={() => toggleCampaign(c.campaign_id)}
                                    />
                                    {c.campaign_name}
                                </label>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Alte HaikuRecommendations-Sektion entfernt 2026-06-12.
                    Pipeline (ads_recommendations) seit 8.6. ohne Cron-Befüllung; die
                    Wochen-Auswertung (Top-3 + 12 Detail-Berichte) deckt denselben
                    Inhalt ab und hat Notiz-Feld pro Bericht für den Workflow. */}

                {/* Haiku-Wochen-Auswertung — ganz oben (Default: eingeklappt) */}
                {BEREICHE.filter(b => b.key === 'haiku_reports').map(({ key, label }) => {
                    const mod = modus[key] || 'single';
                    const res = results[key];
                    const isCollapsed = !!collapsed[key];
                    return (
                        <React.Fragment key={'top-' + key}>
                        <div
                            className={"perf-divider perf-divider-toggle" + (isCollapsed ? ' is-collapsed' : '')}
                            onClick={() => toggleCollapse(key)}
                            title={isCollapsed ? 'Aufklappen' : 'Einklappen'}
                        >
                            <span className="perf-divider-label">{dividerLabels[key] || label} {haikuRangeBadge()}</span>
                            <span className="perf-divider-chevron">{isCollapsed ? '▶' : '▼'}</span>
                        </div>
                        <section className="perf-section" style={isCollapsed ? {display:'none'} : {}}>
                            <div className="perf-section-header">
                                <div className="perf-section-controls">
                                    {res && (
                                        <>
                                            <button className={`perf-btn-small ${mod === 'single' ? 'active' : ''}`} onClick={() => setModus(prev => ({ ...prev, [key]: 'single' }))}>Einzeln</button>
                                            <button className={`perf-btn-small ${mod === 'compare' ? 'active' : ''}`} onClick={() => setModus(prev => ({ ...prev, [key]: 'compare' }))}>Vergleich</button>
                                        </>
                                    )}
                                    <button className="perf-btn" onClick={() => loadBereich(key)} disabled={loading[key] || !selected.length}>
                                        {loading[key] ? 'Laedt...' : 'Laden'}
                                    </button>
                                </div>
                            </div>
                            {errors[key] && <p className="perf-error">{errors[key]}</p>}
                            {res && mod === 'single' && renderHaikuReports(res.p1)}
                        </section>
                        </React.Fragment>
                    );
                })}


                {/* Aktionsplan — Montags-Cockpit */}
                <div
                    className={"perf-divider perf-divider-toggle" + (collapsed['aktionsplan'] ? ' is-collapsed' : '')}
                    onClick={() => toggleCollapse('aktionsplan')}
                    title={collapsed['aktionsplan'] ? 'Aufklappen' : 'Einklappen'}
                >
                    <span className="perf-divider-label">📋 Aktionsplan {renderFreshness('decisions')}</span>
                    <span className="perf-divider-chevron">{collapsed['aktionsplan'] ? '▶' : '▼'}</span>
                </div>
                {!collapsed['aktionsplan'] && (
                    <DecisionsSummary
                        selectedIds={selected}
                        campaignList={campaigns}
                    />
                )}

                {/* Änderungen — ganz oben */}
                {selected.length >= 1 && (
                    <>
                    <div
                        className={"perf-divider perf-divider-toggle" + (collapsed['aenderungen'] ? ' is-collapsed' : '')}
                        onClick={() => toggleCollapse('aenderungen')}
                        title={collapsed['aenderungen'] ? 'Aufklappen' : 'Einklappen'}
                    >
                        <span className="perf-divider-label">📋 Änderungen {renderFreshness('aenderungen')}</span>
                        <span className="perf-divider-chevron">{collapsed['aenderungen'] ? '▶' : '▼'}</span>
                    </div>
                    <div className="perf-section" style={collapsed['aenderungen'] ? {display:'none'} : {}}>
                        <ChangesImport
                            key={'chg-' + selected[0]}
                            campaignId={selected[0]}
                            campaignName={campaigns.find(c => String(c.campaign_id) === String(selected[0]))?.campaign_name || ''}
                            selectedIds={selected}
                            campaignList={campaigns}
                            von1={von1}
                            bis1={bis1}
                        />
                    </div>
                    </>
                )}

                <div
                    className={"perf-divider perf-divider-toggle" + (collapsed['journey'] ? ' is-collapsed' : '')}
                    onClick={() => toggleCollapse('journey')}
                    title={collapsed['journey'] ? 'Aufklappen' : 'Einklappen'}
                >
                    <span className="perf-divider-label">👣 User Journey {renderFreshness('journey')}</span>
                    <span className="perf-divider-chevron">{collapsed['journey'] ? '▶' : '▼'}</span>
                </div>
                <div className="perf-section" style={collapsed['journey'] ? {display:'none'} : {}}>
                    <div
                        className="perf-azg-label perf-azg-toggle"
                        onClick={() => toggleData('journey')}
                        style={{cursor:'pointer', marginBottom:'8px'}}
                    >
                        <span>{collapsedData['journey'] !== false ? '▶' : '▼'}</span>
                        {' '}User Journey anzeigen
                    </div>
                    {collapsedData['journey'] === false && <MiniJourney
                        von1={von1}
                        bis1={bis1}
                        campaignIds={selected}
                    />
                    }
                    <AdsDecisions bereich="journey" selectedIds={selected} campaignList={campaigns} campaignId={selected.length === 1 ? selected[0] : null} />
                </div>

                {BEREICHE.filter(b => b.key !== 'haiku_reports').map(({ key, label }) => {
                    const mod = modus[key] || 'single';
                    const res = results[key];
                    const isCollapsed = !!collapsed[key];
                    return (
                        <React.Fragment key={key}>
                        <div
                            className={"perf-divider perf-divider-toggle" + (isCollapsed ? ' is-collapsed' : '')}
                            onClick={() => toggleCollapse(key)}
                            title={isCollapsed ? 'Aufklappen' : 'Einklappen'}
                        >
                            <span className="perf-divider-label">{dividerLabels[key] || label} {renderFreshness(key)}</span>
                            <span className="perf-divider-chevron">{isCollapsed ? '▶' : '▼'}</span>
                        </div>
                        <section className="perf-section" style={isCollapsed ? {display:'none'} : {}}>
                            <div className="perf-section-header">
                                <h3>{label}</h3>
                                <div className="perf-section-controls">
                                    {res && (
                                        <>
                                            <button
                                                className={`perf-btn-small ${mod === 'single' ? 'active' : ''}`}
                                                onClick={() => setModus(prev => ({ ...prev, [key]: 'single' }))}
                                            >Einzeln</button>
                                            <button
                                                className={`perf-btn-small ${mod === 'compare' ? 'active' : ''}`}
                                                onClick={() => setModus(prev => ({ ...prev, [key]: 'compare' }))}
                                            >Vergleich</button>
                                        </>
                                    )}
                                    {key !== 'suchbegriffe' && (
                                        <button
                                            className="perf-btn"
                                            onClick={() => loadBereich(key)}
                                            disabled={loading[key] || !selected.length}
                                        >
                                            {loading[key] ? 'Laedt...' : 'Laden'}
                                        </button>
                                    )}
                                </div>
                            </div>
                            {errors[key] && <p className="perf-error">{errors[key]}</p>}
                            {res && mod === 'single'  && (
                                key === 'haiku_reports'
                                    ? renderHaikuReports(res.p1)
                                : key === 'assets'
                                    ? renderAssetsTables(res.p1)
                                    : (key === 'age' || key === 'keywords')
                                        ? renderByAZGTable(res.p1, key)
                                        : key === 'schedule'
                                            ? (<>
                                                <div
                                                    className="perf-azg-label perf-azg-toggle"
                                                    onClick={() => toggleData('schedule')}
                                                    style={{cursor:'pointer', marginBottom:'8px'}}
                                                >
                                                    <span>{collapsedData['schedule'] !== false ? '▶' : '▼'}</span>
                                                    {' '}Tabelle anzeigen ({res.p1.length} Zeilen)
                                                </div>
                                                {collapsedData['schedule'] === false && renderSingleTable(res.p1, key)}
                                              </>)
                                            : renderSingleTable(res.p1, key)
                            )}
                            {res && mod === 'compare' && (
                                key === 'assets'
                                    ? renderAssetsCompare(res.p1, res.p2)
                                    : renderCompareTable(res.p1, res.p2, key)
                            )}
                            {key !== 'assets' && key !== 'suchbegriffe' && key !== 'haiku_reports' && (
                                <AdsDecisions
                                    bereich={key}
                                    selectedIds={selected}
                                    campaignList={campaigns}
                                    campaignId={selected.length === 1 ? selected[0] : null}
                                    kontext=""
                                />
                            )}
                            {key === 'auktionen' && selected.length >= 1 && (
                                <AuctionImport
                                    key={selected[0]}
                                    campaignId={selected[0]}
                                    campaignName={campaigns.find(c => String(c.campaign_id) === String(selected[0]))?.campaign_name || ''}
                                    selectedIds={selected}
                                    campaignList={campaigns}
                                    von1={von1}
                                    bis1={bis1}
                                />
                            )}
                            {key === 'standorte' && selected.length >= 1 && (
                                <LocationsImport
                                    key={'loc-' + selected[0]}
                                    campaignId={selected[0]}
                                    campaignName={campaigns.find(c => String(c.campaign_id) === String(selected[0]))?.campaign_name || ''}
                                    selectedIds={selected}
                                    campaignList={campaigns}
                                    von1={von1}
                                    bis1={bis1}
                                />
                            )}
                            {key === 'zielgruppen' && selected.length >= 1 && (
                                <AudiencesImport
                                    key={'aud-' + selected[0]}
                                    campaignId={selected[0]}
                                    campaignName={campaigns.find(c => String(c.campaign_id) === String(selected[0]))?.campaign_name || ''}
                                    selectedIds={selected}
                                    campaignList={campaigns}
                                    von1={von1}
                                    bis1={bis1}
                                />
                            )}
                            {key === 'suchbegriffe' && selected.length >= 1 && (
                                <SearchTermsImport
                                    key={'st-' + selected[0]}
                                    campaignId={selected[0]}
                                    campaignName={campaigns.find(c => String(c.campaign_id) === String(selected[0]))?.campaign_name || ''}
                                    selectedIds={selected}
                                    campaignList={campaigns}
                                    von1={von1}
                                    bis1={bis1}
                                />
                            )}
                        </section>
                        </React.Fragment>
                    );
                })}

                <div className="perf-footer">
                    <a href="/admin" className="perf-btn" style={{textDecoration:'none'}}>← Zurück zum Dashboard</a>
                </div>

            </div>
        </div>
    );
}